/**
 * BRAIN-INTEGRITEIT — zelf-audit van de Code Registry curatie-laag.
 *
 * De Code Registry is "de hersenen" van het governance-systeem: het verklaart
 * issue-codes, hun levenscyclus, en de handgecureerde DIDACTISCHE RELATIES
 * (paren / clusters / botsingen), de ROLLUP-KANDIDATEN, plus de TRANSITIONELE
 * / SUNSET-codes (migratie-tellers & deprecaties die hun waarde verliezen
 * zodra de migratie klaar is). Alle gecureerde kaarten verwijzen naar
 * issue-codes als KALE STRINGS.
 *
 * Niets garandeert dat die strings nog échte `ISSUE_CODES` zijn. Als een code
 * in `codes.ts` ooit hernoemd of verwijderd wordt, zou de registry stilletjes
 * een relatie/rollup/transitie blijven tonen die naar een FANTOOM-code wijst —
 * een verouderde, misleidende kaart, precies het tegenovergestelde van een
 * betrouwbaar brein.
 *
 * Deze guard valideert élke gerefereerde code tegen de canonieke
 * `ISSUE_CODES`-set en maakt drift zichtbaar in de UI. Read-only: verandert
 * geen pipeline-gedrag, voegt alleen OBSERVABILITY toe op de analyse-laag zelf.
 * Conform het plan-principe "proxies/grenzen moeten observable zijn én
 * boundaries zijn first-class citizens" (ARCHITECTURAL_PEER_REVIEW_PLAN_2026,
 * Fundament F0 + Deel II overkoepelend principe).
 */
import { ISSUE_CODES } from '../../../governance/pipeline/issues/codes';

/** Waar een fantoom-referentie vandaan komt, zodat een mens de bron kan vinden. */
export type RegistryIntegritySource = 'relation' | 'rollup-member' | 'transitional' | 'transitional-merge-target';
export interface RegistryIntegrityProblem {
  /** De gerefereerde code die nergens in ISSUE_CODES bestaat. */
  code: string;
  source: RegistryIntegritySource;
  /** Het label van de relatie/rollup/transitie waarin de fantoom-code zit. */
  context: string;
}

/** Minimale vorm van een didactische relatie die we hoeven te valideren. */
export interface IntegrityRelationLike {
  label: string;
  members: string[];
}

/** Minimale vorm van een rollup-familie die we hoeven te valideren. */
export interface IntegrityRollupLike {
  title: string;
  memberCodes: string[];
}

/**
 * Minimale vorm van een transitionele/sunset-code die we hoeven te valideren.
 * Anders dan een rollup-`proposedCode` (die juist NIEUW mag zijn), MOET een
 * transitionele code al bestaan — hij wijst naar een levende migratie-teller of
 * deprecatie in `codes.ts`. Een fantoom hier betekent dat de teller hernoemd of
 * verwijderd is en de sunset-kaart achterloopt.
 */
export interface IntegrityTransitionalLike {
  label: string;
  code: string;
  /**
   * Optioneel: de code waarin deze transitionele code conceptueel kan opgaan
   * (de "kan opgaan in"-suggestie op de sunset-kaart). Anders dan een rollup-
   * `proposedCode` (een mogelijk NIEUWE canonieke code) wijst een mergeTarget
   * naar een BESTAANDE code — de admin krijgt 'm als concreet doel
   * gepresenteerd. Wordt dus óók gevalideerd: een fantoom-mergeTarget zou een
   * sunset-kaart een merge laten voorstellen naar een code die niet meer
   * bestaat.
   */
  mergeTarget?: string;
}

/**
 * Loop de gecureerde kaarten af en verzamel elke lid-code die niet (meer) in de
 * canonieke ISSUE_CODES-set zit.
 *
 * BELANGRIJK: de `proposedCode` van een rollup wordt BEWUST niet gevalideerd —
 * dat mag een nieuwe canonieke code zijn die nog niet bestaat (dat is de hele
 * point van een rollup-voorstel). Alleen de leden die zogenaamd al bestaan
 * moeten kloppen. Transitionele codes verwijzen daarentegen altijd naar een
 * bestaande teller/deprecatie en worden dus wél gevalideerd.
 */
export function computeRegistryIntegrity(relations: IntegrityRelationLike[], rollups: IntegrityRollupLike[], transitional: IntegrityTransitionalLike[] = []): RegistryIntegrityProblem[] {
  const known = new Set<string>(Object.values(ISSUE_CODES));
  const problems: RegistryIntegrityProblem[] = [];
  for (const rel of relations) {
    for (const code of rel.members) {
      if (!known.has(code)) {
        problems.push({
          code,
          source: 'relation',
          context: rel.label
        });
      }
    }
  }
  for (const fam of rollups) {
    for (const code of fam.memberCodes) {
      if (!known.has(code)) {
        problems.push({
          code,
          source: 'rollup-member',
          context: fam.title
        });
      }
    }
  }
  for (const t of transitional) {
    if (!known.has(t.code)) {
      problems.push({
        code: t.code,
        source: 'transitional',
        context: t.label
      });
    }
    // De "kan opgaan in"-suggestie wijst naar een bestaande code; valideer 'm
    // zodat een hernoemd/verwijderd merge-doel geen fantoom-suggestie wordt.
    if (t.mergeTarget && !known.has(t.mergeTarget)) {
      problems.push({
        code: t.mergeTarget,
        source: 'transitional-merge-target',
        context: t.label
      });
    }
  }
  return problems;
}